import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { RoutingAnalyticsPanel } from '@frontend/analytics-phone-routing';
import { pageStyles } from '@frontend/analytics-shared';
import { BadgeBanner } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Tabs } from '@frontend/design-system';
import { defaultBarChartAppearance } from '../constants';
import { useFetchAggregatedCallsSummary } from '../hooks/use-fetch-aggregated-calls-summary';
import { usePhoneAnalyticsShallowStore } from '../hooks/use-phone-analytics-store';
import { trackingIds } from '../tracking-ids';
import { CallAnalyticsPanel } from './call-analytics-panel';
import { CallReportSummary } from './call-reports-summary';
import { DurationAnalyticsPanel } from './duration-analytics-panel';
import { SmallModeSummaryCards } from './small-mode-summary-cards';

const DEFAULT_TAB_VIEW = 'call-analytics-view';

export const PhoneReportCharts = () => {
  const [activeTab, setActiveTab] = useState<string>(DEFAULT_TAB_VIEW);
  const { t } = useTranslation('analytics-phone');

  const { isSmallMode } = useIsSmallMode();
  const aggregatedCallSummary = useFetchAggregatedCallsSummary();

  const { filters, isDemoAccount, filterHintText, previousGlsLocationIds, demoData, accessibleLocationsNames } =
    usePhoneAnalyticsShallowStore(
      'filters',
      'isDemoAccount',
      'filterHintText',
      'previousGlsLocationIds',
      'demoData',
      'accessibleLocationsNames'
    );

  const routingOverrides = useMemo(
    () => ({
      filters,
      isDemoAccount,
      filterHintText,
      previousGlsLocationIds,
      demoData,
      accessibleLocationsNames,
      isEmbedded: false,
    }),
    [filters, isDemoAccount, filterHintText, previousGlsLocationIds, demoData, accessibleLocationsNames]
  );

  if (isSmallMode) {
    return (
      <div css={styles.wrapper}>
        <SmallModeSummaryCards aggregatedCallSummary={aggregatedCallSummary} />
        <CallAnalyticsPanel defaultChartAppearance={defaultBarChartAppearance} />
      </div>
    );
  }

  return (
    <div css={styles.wrapper}>
      <CallReportSummary aggregatedCallSummary={aggregatedCallSummary} />

      <Tabs initialTab={DEFAULT_TAB_VIEW} onChange={setActiveTab}>
        <div css={styles.tabsWrapper}>
          <Tabs.Bar css={pageStyles.customTabSelect}>
            <Tabs.Tab id='call-analytics-view' trackingId={trackingIds.callAnalyticsTab}>
              {t('Call Analytics')}
            </Tabs.Tab>
            <Tabs.Tab id='duration-analytics-view' trackingId={trackingIds.durationAnalyticsTab}>
              {t('Duration Analytics')}
            </Tabs.Tab>
            <Tabs.Tab id='routing-analytics-view' trackingId={trackingIds.routingAnalyticsTab}>
              <BetaBanner name={t('Routing Analytics')} />
            </Tabs.Tab>
          </Tabs.Bar>
        </div>

        <Tabs.Panel css={styles.tabPanel} controller='call-analytics-view'>
          {activeTab === 'call-analytics-view' && (
            <CallAnalyticsPanel defaultChartAppearance={defaultBarChartAppearance} />
          )}
        </Tabs.Panel>
        <Tabs.Panel css={styles.tabPanel} controller='duration-analytics-view'>
          {activeTab === 'duration-analytics-view' && (
            <DurationAnalyticsPanel defaultChartAppearance={defaultBarChartAppearance} />
          )}
        </Tabs.Panel>
        <Tabs.Panel css={styles.tabPanel} controller='routing-analytics-view'>
          {activeTab === 'routing-analytics-view' && <RoutingAnalyticsPanel overrides={routingOverrides} />}
        </Tabs.Panel>
      </Tabs>
    </div>
  );
};

const BetaBanner = ({ name }: { name: string }) => {
  const { t } = useTranslation('analytics-phone');
  return (
    <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
      {name}
      <BadgeBanner.AnimatedBadge animated={false} bgColor={theme.colors.indigo50} label={t('Beta')} />
    </div>
  );
};

const styles = {
  wrapper: css`
    overflow: auto;
  `,

  tabPanel: css`
    height: 100%;
    overflow: auto;
  `,

  tabsWrapper: css`
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-bottom: ${theme.spacing(1)};
    padding-bottom: ${theme.spacing(2)};
    top: ${theme.spacing(-2)};
    z-index: 1;
  `,
};
